'use client';
import React, { useState } from 'react';
import { Plus, Settings, Pencil, CheckCircle } from 'lucide-react';

export default function PaymentMethodsManagers() {
  const [paymentMethods, setPaymentMethods] = useState([
    {
      friendlyName: 'Check / Money Order',
      systemName: 'Payments.CheckMoneyOrder',
      supportsCapture: 'No',
      refund: 'Not supported',
      partialRefund: 'Not supported',
      void: 'Not supported',
      recurring: 'Not supported',
      displayOrder: 1,
      isActive: true,
    },
    {
      friendlyName: 'Credit Card',
      systemName: 'Payments.Manual',
      supportsCapture: 'Manual',
      refund: 'Manual',
      partialRefund: 'Manual',
      void: 'Manual',
      recurring: 'Manual',
      displayOrder: 2,
      isActive: true,
    },
  ]);

  const [showForm, setShowForm] = useState(false);
  const [newMethod, setNewMethod] = useState({
    friendlyName: '',
    systemName: '',
    displayOrder: '',
    isActive: false,
  });

  const handleAdd = () => {
    setPaymentMethods([...paymentMethods, newMethod]);
    setNewMethod({
      friendlyName: '',
      systemName: '',
      displayOrder: '',
      isActive: false,
    });
    setShowForm(false);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Recommended PayPal Section */}
      <div className="bg-white shadow rounded-xl p-6 border border-gray-200 mb-6 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        {/* Left Side */}
        <div className="flex items-start gap-4">
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg"
            alt="PayPal Logo"
            className="w-20 h-auto"
          />
          <div>
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              Recommended
              <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                PayPal
              </span>
            </h2>
            <p className="text-gray-600 mt-2">
              <strong>PayPal:</strong> The all-in-one checkout solution.
            </p>
            <ul className="text-sm text-gray-500 list-disc ml-5 mt-2 space-y-1">
              <li>No setup or monthly fees for businesses.</li>
              <li>Buy now, pay later at no extra cost.</li>
              <li>Accept credit/debit cards securely worldwide.</li>
            </ul>
          </div>
        </div>

        {/* Right Side (UPI/Setup Button) */}
        <div className="flex flex-col md:items-end md:text-right gap-3">
          <button className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow">
            Connect with PayPal
          </button>
          <button className="px-5 py-2 border border-green-500 text-green-600 rounded-lg hover:bg-green-50">
            Add UPI Payment
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Payment Methods</h1>
          <p className="text-sm text-gray-500">
            Learn more about payment methods. You can download more plugins in our marketplace.
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="mt-3 md:mt-0 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Plus size={18} />
          Add New
        </button>
      </div>

      {/* Add New Form */}
      {showForm && (
        <div className="bg-white p-4 rounded-lg shadow mb-6 border border-gray-200">
          <h2 className="text-lg font-semibold mb-2">Add New Payment Method</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Friendly Name"
              value={newMethod.friendlyName}
              onChange={(e) =>
                setNewMethod({ ...newMethod, friendlyName: e.target.value })
              }
              className="border rounded p-2 w-full"
            />
            <input
              type="text"
              placeholder="System Name"
              value={newMethod.systemName}
              onChange={(e) =>
                setNewMethod({ ...newMethod, systemName: e.target.value })
              }
              className="border rounded p-2 w-full"
            />
            <input
              type="number"
              placeholder="Display Order"
              value={newMethod.displayOrder}
              onChange={(e) =>
                setNewMethod({ ...newMethod, displayOrder: e.target.value })
              }
              className="border rounded p-2 w-full"
            />
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={newMethod.isActive}
                onChange={(e) =>
                  setNewMethod({ ...newMethod, isActive: e.target.checked })
                }
              />
              <label>Active</label>
            </div>
          </div>
          <button
            onClick={handleAdd}
            className="mt-4 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            Save Payment Method
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-lg shadow border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-4 py-2 text-left">Friendly Name</th>
              <th className="px-4 py-2 text-left">System Name</th>
              <th className="px-4 py-2 text-left">Supports Capture</th>
              <th className="px-4 py-2 text-left">Refund</th>
              <th className="px-4 py-2 text-left">Partial Refund</th>
              <th className="px-4 py-2 text-left">Void</th>
              <th className="px-4 py-2 text-left">Recurring</th>
              <th className="px-4 py-2 text-left">Display Order</th>
              <th className="px-4 py-2 text-left">Active</th>
              <th className="px-4 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paymentMethods.map((method, idx) => (
              <tr key={idx} className="border-b hover:bg-gray-50">
                <td className="px-4 py-2">{method.friendlyName}</td>
                <td className="px-4 py-2">{method.systemName}</td>
                <td className="px-4 py-2">{method.supportsCapture}</td>
                <td className="px-4 py-2">{method.refund}</td>
                <td className="px-4 py-2">{method.partialRefund}</td>
                <td className="px-4 py-2">{method.void}</td>
                <td className="px-4 py-2">{method.recurring}</td>
                <td className="px-4 py-2">{method.displayOrder}</td>
                <td className="px-4 py-2">
                  {method.isActive ? (
                    <CheckCircle className="text-green-600" size={18} />
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-4 py-2 flex gap-2">
                  <button className="text-blue-600 hover:underline flex items-center gap-1">
                    <Settings size={16} /> Configure
                  </button>
                  <button className="text-gray-600 hover:underline flex items-center gap-1">
                    <Pencil size={16} /> Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex justify-between items-center text-sm text-gray-500 mt-4">
        <p>Showing {paymentMethods.length} of {paymentMethods.length} items</p>
        <div className="flex items-center gap-2">
          <span>Show</span>
          <select className="border rounded p-1">
            <option>15</option>
            <option>30</option>
          </select>
          <span>items</span>
        </div>
      </div>
    </div>
  );
}
